import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/ui';
import type { AssetBalanceDto, AutoWithdrawalNetworkCode } from '@shared/api/contracts';
import type { AutoWithdrawalRuleDraft } from '@entities/withdrawal';
import { SINGLE_USDT_NETWORKS } from '@entities/withdrawal';
import type { AutoWithdrawalRuleFieldErrors } from '../../lib/validation';
import { sanitizeWithdrawalAddressInput } from '../../lib/manual-form';
import { getUsdtNetworkLabel, getNetworkChainName } from '../../lib/usdt-network-meta';
import { AmountStrategyFields } from './amount-strategy-fields';
import { AutoWithdrawAddressHint } from './auto-withdraw-address-hint';
import type { WithdrawalAddressItem } from '@shared/api/contracts/security';
import { AddressChangeNotice, RuleAddressState } from './verification-badge';
import { BalanceSummaryPanel } from './balance-summary-panel';
import { SwapFeesNotice } from './swap-fees-notice';
import { ThresholdHint } from './threshold-hint';

interface SingleRuleFormProps {
  draft: AutoWithdrawalRuleDraft;
  assetBalances: AssetBalanceDto[];
  totalBalance?: number;
  availableBalance?: number;
  disabled?: boolean;
  errors?: AutoWithdrawalRuleFieldErrors;
  addresses?: WithdrawalAddressItem[];
  language?: string;
  onNetworkChange: (networkCode: AutoWithdrawalNetworkCode) => void;
  onAddressChange: (address: string) => void;
  onAmountModeChange: (mode: AutoWithdrawalRuleDraft['amountMode']) => void;
  onThresholdChange: (value: string) => void;
  onReserveChange: (value: string) => void;
}

export const SingleRuleForm = ({
  draft,
  assetBalances,
  totalBalance,
  availableBalance,
  disabled = false,
  errors,
  addresses = [],
  language = 'en',
  onNetworkChange,
  onAddressChange,
  onAmountModeChange,
  onThresholdChange,
  onReserveChange,
}: SingleRuleFormProps) => {
  const { t } = useTranslation('cabinet');

  const otherNetworks = useMemo(
    () => SINGLE_USDT_NETWORKS.filter((network) => network !== draft.networkCode),
    [draft.networkCode],
  );

  const networkHint = t('withdrawal.auto.single.networkHint', {
    others: otherNetworks.join(` ${t('withdrawal.auto.single.networkAnd')} `),
    chain: getNetworkChainName(draft.networkCode),
    erc20Note:
      draft.networkCode === 'ERC20' ? t('withdrawal.auto.single.networkHintErc20') : '',
  });

  return (
    <div className="space-y-5">
      <BalanceSummaryPanel assetBalances={assetBalances} totalBalance={totalBalance} availableBalance={availableBalance} />

      <div className="space-y-2">
        <Label htmlFor="auto-single-network">{t('withdrawal.auto.single.receiveNetwork')}</Label>
        <Select
          value={draft.networkCode}
          disabled={disabled}
          onValueChange={(value) => onNetworkChange(value as AutoWithdrawalNetworkCode)}
        >
          <SelectTrigger id="auto-single-network">
            <SelectValue placeholder={t('withdrawal.auto.modal.networkPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {SINGLE_USDT_NETWORKS.map((network) => (
              <SelectItem key={network} value={network}>
                {getUsdtNetworkLabel(network)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{networkHint}</p>
        {errors?.networkCode && <p className="text-xs text-destructive">{errors.networkCode}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="auto-single-address">{t('withdrawal.auto.fields.address')}</Label>
        <Input
          id="auto-single-address"
          value={draft.destinationAddress}
          disabled={disabled}
          onChange={(event) => onAddressChange(sanitizeWithdrawalAddressInput(event.target.value))}
          invalid={Boolean(errors?.destinationAddress)}
          className="font-mono text-[13px]"
          spellCheck={false}
          autoComplete="off"
        />
        <AutoWithdrawAddressHint
          address={draft.destinationAddress}
          networkCode={draft.networkCode}
          fieldError={errors?.destinationAddress}
          onSwitchNetwork={(network) => {
            if (network === 'TRC20' || network === 'ERC20' || network === 'BEP20') onNetworkChange(network);
          }}
        />
        <AddressChangeNotice draft={draft} />
        <RuleAddressState draft={draft} items={addresses} language={language} />
      </div>

      <AmountStrategyFields
        draft={draft}
        disabled={disabled}
        thresholdError={errors?.thresholdAmount}
        reserveError={errors?.reserveAmount}
        amountModeError={errors?.amountMode}
        onAmountModeChange={onAmountModeChange}
        onThresholdChange={onThresholdChange}
        onReserveChange={onReserveChange}
        thresholdId="auto-single-threshold"
        reserveId="auto-single-reserve"
        amountModeId="auto-single-amount-mode"
        thresholdHint={
          <ThresholdHint
            thresholdAmount={draft.thresholdAmount}
            assetCode={draft.assetCode}
            totalBalance={totalBalance}
            balanceAmount={availableBalance}
          />
        }
      />

      <SwapFeesNotice
        draft={draft}
        assetBalances={assetBalances}
        totalBalance={totalBalance}
        targetNetwork={draft.networkCode}
      />
    </div>
  );
};
